import {
  ListSubheader,
  List,
  Grid,
  Paper,
  ListItemButton,
} from '@mui/material';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { analyzerChanged } from '../analyzers/analyzerSlice';
import { toggleSingleTraceView } from 'features/walls/wallsSlice';
import { subscribeToAnalyzers } from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';
import SingleTraceViewDialog from './SingleTraceViewDialog';
import AnalyzerSnapshotsDialog from './AnalyzerSnapshotsDialog';
import { filterName, sortByName } from './utils/searchUtils';
import { Snapshot } from 'types';

import { useAnalyzers } from 'features/analyzers/api/getAnalyzers';
import { useAllSnapshotsWithoutCrosspol } from 'features/snapshots/api/getAllSnapshotsWithoutCrosspol';
import { useWalls } from '../walls/api/getWalls';
import { useWeatherWalls } from '../weatherWalls/api/getWeatherWalls';
import { useNavigate } from 'react-router-dom';
import { useSystemModules } from '../systemModules/api/getSystemModules';

interface Props {
  searchText: string;
  handleClose: () => void;
}

const SearchList = ({ searchText, handleClose }: Props) => {
  const dispatch = useAppDispatch();
  const { data: modules } = useSystemModules({});
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [singleTraceViewOpen, setSingleTraceViewOpen] = useState(false);
  const [analyzerSnapshotsOpen, setAnalyzerSnapshotsOpen] = useState(false);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(-1);
  const [selectedAnalyzerId, setSelectedAnalyzerId] = useState(-1);
  const { data: allSnapshots } = useAllSnapshotsWithoutCrosspol({
    config: { cacheTime: 0 },
  });
  const { data: allAnalyzers } = useAnalyzers({});
  const { data: allWalls } = useWalls(user?.id ? user.id : -1);
  const { data: allWeatherWalls } = useWeatherWalls(user?.id ? user.id : -1);
  const filteredSnapshots = allSnapshots
    ?.filter((snapshot) => filterName(searchText, snapshot))
    .sort(sortByName);
  const filteredAnalyzers = allAnalyzers
    ?.filter((analyzer) => filterName(searchText, analyzer))
    .sort(sortByName);
  const filteredWalls = allWalls
    ?.filter((wall) => filterName(searchText, wall))
    .sort(sortByName);
  const filteredWeatherWalls = allWeatherWalls
    ?.filter((wall) => filterName(searchText, wall))
    .sort(sortByName);
  const handleOpenSingleTraceView = (snapshot: Snapshot) => {
    if (!user) return;
    dispatch(
      subscribeToAnalyzers({
        user_id: user.id,
        analyzer_ids: [snapshot.analyzer_id],
      })
    );
    setSelectedSnapshotId(snapshot.id);
    setSelectedAnalyzerId(snapshot.analyzer_id);
    dispatch(analyzerChanged(snapshot.analyzer_id));
    setSingleTraceViewOpen(true);
  };
  const handleCloseSingleTraceView = () => {
    setSingleTraceViewOpen(false);
    dispatch(toggleSingleTraceView(false));
    handleClose();
  };
  const handleOpenAnalyzerSnapshots = (analyzerId: number) => {
    dispatch(analyzerChanged(analyzerId));
    setSelectedAnalyzerId(analyzerId);
    setAnalyzerSnapshotsOpen(true);
  };
  const handleCloseAnalyzerSnapshots = () => {
    setAnalyzerSnapshotsOpen(false);
    handleClose();
  };
  return (
    <Grid container style={{ maxHeight: '75vh' }}>
      <Grid container item component={Paper}>
        <List
          sx={{
            minWidth: '15rem',
          }}
        >
          <ListSubheader>Snapshots</ListSubheader>
          {filteredSnapshots?.map((snapshot) => (
            <ListItemButton
              key={snapshot.id}
              onClick={() => handleOpenSingleTraceView(snapshot)}
            >
              {snapshot.name} (
              {allAnalyzers?.find((a) => a.id === snapshot.analyzer_id)?.name})
            </ListItemButton>
          ))}
          <ListSubheader>Analyzers</ListSubheader>
          {filteredAnalyzers?.map((analyzer) => (
            <ListItemButton
              key={analyzer.id}
              onClick={() => handleOpenAnalyzerSnapshots(analyzer.id)}
            >
              {analyzer.name}
            </ListItemButton>
          ))}
          <ListSubheader>Walls</ListSubheader>
          {filteredWalls?.map((wall) => (
            <ListItemButton
              key={wall.id}
              onClick={() => {
                handleCloseSingleTraceView();
                navigate('/wall/' + wall.id);
              }}
            >
              {wall.name}
            </ListItemButton>
          ))}
          {modules?.WEATHER && (
            <>
              <ListSubheader>Weather Walls</ListSubheader>
              {filteredWeatherWalls?.map((wall) => (
                <ListItemButton
                  key={wall.id}
                  onClick={() => {
                    handleCloseSingleTraceView();
                    navigate('/weather-wall/' + wall.id);
                  }}
                >
                  {wall.name}
                </ListItemButton>
              ))}
            </>
          )}
        </List>
        <SingleTraceViewDialog
          snapshotId={selectedSnapshotId}
          setSnapshotId={setSelectedSnapshotId}
          analyzerId={selectedAnalyzerId}
          open={singleTraceViewOpen}
          handleClose={handleCloseSingleTraceView}
          traceMode={0}
        />
        <AnalyzerSnapshotsDialog
          open={analyzerSnapshotsOpen}
          handleClose={handleCloseAnalyzerSnapshots}
          analyzerId={selectedAnalyzerId}
        />
      </Grid>
    </Grid>
  );
};

export default SearchList;
